import { AfterViewInit, Component, ComponentFactoryResolver, ComponentRef, ElementRef, Input, OnDestroy, OnInit, Type, ViewChild } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faGripLines } from '@fortawesome/pro-light-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { Store } from '@ngxs/store';
import { TooltipDirective } from '@progress/kendo-angular-tooltip/dist/es2015/tooltip/tooltip.directive';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ActionSpecificSettingsComponent } from 'src/app/components/bricks/layout/toolbar/toolbar-settings/action-specific-settings/action-specific-settings-component';
import { MakeCallActionSettingsComponent } from 'src/app/components/bricks/layout/toolbar/toolbar-settings/action-specific-settings/make-call-action-settings/make-call-action-settings.component';
import { OpenDynamicLinkSettingsComponent } from 'src/app/components/bricks/layout/toolbar/toolbar-settings/action-specific-settings/open-dynamic-link-settings/open-dynamic-link-settings.component';
import { RequestUserStatusActionSettingsComponent } from 'src/app/components/bricks/layout/toolbar/toolbar-settings/action-specific-settings/request-user-status/request-user-status-action-settings.component';
import { RequeueActionSettingsComponent } from 'src/app/components/bricks/layout/toolbar/toolbar-settings/action-specific-settings/requeue-action-settings/requeue-action-settings.component';
import { SetTagActionSettingsComponent } from 'src/app/components/bricks/layout/toolbar/toolbar-settings/action-specific-settings/set-tag-action-settings/set-tag-action-settings.component';
import { ComponentHostDirective } from 'src/app/components/general-directives/component-host.directive';
import { UserActionTyp } from 'src/app/samwebDescription/samweb/userActions/userActions';
import {
   ChangeActionColorForToolbar,
   ChangeActionIconForToolbar,
   ChangeActionTitleForToolbar,
   ChangeActionTypeForToolbarAction,
   RemoveActionForToolbar,
} from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/toolbar-settings/toolbar-settings.actions';
import { ToolbarSettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/toolbar-settings/toolbar-settings.state';
import { UserActionRemoteDto } from 'src/generated/swagger';

@Component({
   selector: 'samweb-toolbar-item-setting',
   templateUrl: './toolbar-item-setting.component.html',
   styleUrls: ['./toolbar-item-setting.component.scss'],
})
export class ToolbarItemSettingComponent implements OnInit, OnDestroy, AfterViewInit {
   private destroy$ = new Subject();

   @Input()
   public brickInstanceId: string;

   @Input()
   public toolbarId: string;

   @Input()
   public actionId: string;

   @Input()
   public state: any;

   @Input()
   public collapsed = false;

   @ViewChild('actionSelectionTooltipDirective')
   public brickSelectionTooltipDirective: TooltipDirective;

   @ViewChild('actionSelectionPopupControl', { read: ElementRef })
   public brickSelectionPopupControl: ElementRef;

   public userActionPossibleValues$ = UserActionTyp.selectableActions$(this.store);

   public actionContent$: Observable<UserActionRemoteDto>;

   public dragIcon: IconDefinition = faGripLines;
   public actionTitleTranslation$: Observable<string>;
   public removeTabIcon: IconDefinition = faTimes;
   public actionTyp$: Observable<UserActionTyp>;
   public iconName$: Observable<string>;
   @ViewChild(ComponentHostDirective, { static: false })
   componentHost: ComponentHostDirective;

   constructor(public store: Store, private componentFactoryResolver: ComponentFactoryResolver) {}

   ngOnInit() {
      this.actionContent$ = this.store
         .select(ToolbarSettingsState.getToolbarActionFn)
         .pipe(map((fn) => fn(this.brickInstanceId, this.toolbarId, this.actionId)));
      this.actionTyp$ = this.store
         .select(ToolbarSettingsState.getActionTypeForUserActionFn)
         .pipe(map((fn) => fn(this.brickInstanceId, this.toolbarId, this.actionId)));
      this.iconName$ = this.store.select(ToolbarSettingsState.getActionIconFn).pipe(map((fn) => fn(this.brickInstanceId, this.toolbarId, this.actionId)));
      this.actionTitleTranslation$ = this.actionTyp$.pipe(switchMap((x) => x.getTranslationKey$(this.store)));
   }

   ngAfterViewInit() {
      this.actionTyp$
         .pipe(
            filter((actionTyp) => !_.isNil(actionTyp)),
            distinctUntilChanged((prev, curr) => prev.value === curr.value),
            tap(() => {
               const viewContainerRef = this.componentHost.viewContainerRef;
               viewContainerRef.clear();
            }),
            map((actionTyp) => {
               switch (actionTyp) {
                  case UserActionTyp.REQUEUE_CALL:
                     return RequeueActionSettingsComponent as Type<ActionSpecificSettingsComponent>;
                  case UserActionTyp.REQUEST_USER_STATUS:
                     return RequestUserStatusActionSettingsComponent as Type<ActionSpecificSettingsComponent>;
                  case UserActionTyp.OPEN_DYNAMIC_LINK:
                     return OpenDynamicLinkSettingsComponent as Type<ActionSpecificSettingsComponent>;
                  case UserActionTyp.MAKE_CALL:
                     return MakeCallActionSettingsComponent as Type<ActionSpecificSettingsComponent>;
                  case UserActionTyp.SET_TAG:
                     return SetTagActionSettingsComponent as Type<ActionSpecificSettingsComponent>;
                  default:
                     return null;
               }
            }),
            filter((componentType) => !_.isNil(componentType)),
            tap((componentType) => {
               const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentType);
               const viewContainerRef = this.componentHost.viewContainerRef;
               const componentRef = viewContainerRef.createComponent(componentFactory) as ComponentRef<ActionSpecificSettingsComponent>;
               componentRef.instance.brickInstanceId = this.brickInstanceId;
               componentRef.instance.toolbarId = this.toolbarId;
               componentRef.instance.actionId = this.actionId;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   actionTitleChanged(value: string) {
      this.store.dispatch(new ChangeActionTitleForToolbar(this.brickInstanceId, this.toolbarId, this.actionId, value));
   }

   actionColorChanged(color: string) {
      this.store.dispatch(new ChangeActionColorForToolbar(this.brickInstanceId, this.toolbarId, this.actionId, color));
   }

   clearActionColor() {
      this.store.dispatch(new ChangeActionColorForToolbar(this.brickInstanceId, this.toolbarId, this.actionId, null));
   }

   actionIconChanged(value: IconDefinition) {
      this.store.dispatch(new ChangeActionIconForToolbar(this.brickInstanceId, this.toolbarId, this.actionId, value));
   }

   removeAction() {
      this.store.dispatch(new RemoveActionForToolbar(this.brickInstanceId, this.toolbarId, this.actionId));
   }

   changeActionTyp(actionTyp: UserActionTyp) {
      this.store.dispatch(new ChangeActionTypeForToolbarAction(this.brickInstanceId, this.toolbarId, this.actionId, actionTyp));
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
